// (C) 2022 GoodData Corporation
import { invariant } from "ts-invariant";
import { FixedLiteralFormatter, FormatterUsingIcu, InvalidArgumentError, } from "./fmt_base.js";
import { DefaultFormat } from "./gooddata_format.js";
import { DefaultFormatterMatchers } from "./regex.js";
/**
 * Represents one of the alternatives for the conditional format.
 */
export class FormatAlternative {
    constructor(op, rhs, formatter) {
        this.op = op;
        this.rhs = rhs;
        this.formatter = formatter;
        // if this bombs then there is a problem in conditional format compilation;
        // only null op can come with an empty rhs (if there is any, it will be ignored)
        invariant(rhs !== null || op === "Equal");
    }
    /**
     * @returns true if this alternative is for [=null] condition
     */
    isNullAlternative() {
        return this.rhs === null;
    }
    /**
     * Evaluates whether this alternative matches the number to format.
     *
     * @param number - number to format
     * @param legacyCompatibility - true if evaluation should be compatible with legacy (reference) behavior;
     *  see doc for the legacy compatibility option for more
     * @returns true if matches and should be used to format the number, false if not
     */
    evaluate(number, legacyCompatibility) {
        if (number === null) {
            // matching NULL values; this is tricky due to legacy behavior that falls into holes and coercing
            // leads it to 'interesting' conclusions
            if (this.rhs === null) {
                // there is only one invariant where an alternative can have a null RHS - that is
                // when the operator is equals (see assertion is the alternative constructor)
                return true;
            }
            // without legacy compatibility, the only condition that can match null is [=null]
            // this is very reasonable :)
            if (!legacyCompatibility) {
                return false;
            }
            // now the fun starts; to be compatible with the reference implementation, the code
            // has to mimic how JavaScript compares an empty string (reference impl's representation of null value :/)
            // with numbers:
            //
            // "" >= <negative number or zero> --> true
            // "" > <negative number>          --> true
            // "" <= <positive number or zero> --> true
            // "" < <positive number>          --> true
            //
            // Thus, if the number to format is null then:
            //
            // >= will match for rhs that is negative or zero
            // >  will match for a negative rhs
            // <= will match for rhs that is positive or zero
            // <  will match for a positive rhs
            //
            // apart from this, there is also one weird case / hole in the reference implementation;
            // [=0.0] leads to comparison "" == 0 - which evaluates true, while [=0] leads to comparison "" === 0
            // which works as one would expect
            //
            // if the format string uses [=0.0] condition, this is represented as 'WeakEqual' operator with RHS 0
            switch (this.op) {
                case "Equal":
                    return false;
                case "WeakEqual":
                    return this.rhs === 0;
                case "LessThan":
                    return this.rhs > 0;
                case "LessThanOrEqual":
                    return this.rhs >= 0;
                case "GreaterThan":
                    return this.rhs < 0;
                case "GreaterThanOrEqual":
                    return this.rhs <= 0;
                default:
                    return false;
            }
        }
        // at this point there is some number on the left-hand-side; compare with the right-hand side accordingly
        switch (this.op) {
            case "Equal":
            case "WeakEqual":
                // non-null number can only equal to non-null rhs; this is true for both normal and
                // weak equality; weak equality only kicks in if the number to format is null; null number
                // handling is covered fully in the initial condition
                return this.rhs !== null && number === this.rhs;
            case "GreaterThan":
                return this.rhs !== null && number > this.rhs;
            case "GreaterThanOrEqual":
                return this.rhs !== null && number >= this.rhs;
            case "LessThan":
                return this.rhs !== null && number < this.rhs;
            case "LessThanOrEqual":
                return this.rhs !== null && number <= this.rhs;
            default:
                return false;
        }
    }
    doFormat(ctx, number, metadata) {
        return this.formatter.doFormat(ctx, number, metadata);
    }
    doResolve(ctx, number) {
        return this.formatter.doResolve(ctx, number);
    }
}
/**
 * Format strings that include conditionals are essentially composite format strings. They are multiple
 * format strings, one for different alternatives.
 *
 * First rule for conditional format is that it will always work and format *absolute value* of the number to format.
 * This is to allow alternatives for negative numbers to represent the negativeness as they see fit.
 *
 * The conditional format strings have some flexibility to them. Let's start with the basics - implicit conditional
 * format. Implicit conditional format can contain up to 3 format strings separated by semicolon (;):
 *
 * - first format string is used for values `>=` 0
 * - second format string is used for values `<` 0
 * - third format string is used for values = 0
 *
 * So for instance: "good ##.##;bad ##.##;okay" would format 1 as "good 1", -1 as "bad 1" and 0 as "okay"
 *
 * Apart, and on top of the implicit conditions, the format string can also contain explicit conditions. The
 * explicit condition specifies operator and right-hand-side value and then the format string. So for instance
 * "[=1000]#, grand" explicit alternative would format number 1000 as "1 grand".
 *
 * The supported operators are the standard: =, `<`, `\>`, `<=`, `\>=`. The right hand side is a positive or negative integer or
 * "NULL". The "NULL" rhs only makes sense in combination with equals operator.
 *
 * The null conditions are useful to provide special literals to use when the value to format is NULL (NULL as in
 * not present at all, not nullptr). The format string provided in the null condition is always treated literally.
 *
 * Now the fun part is that it is also possible to combine both explicit and implicit alternatives. So you can do
 * something like this: [=13]fun;[=666]more fun;[`\>=`100000]#,,.00M;[`\>`1000]##,k;##;-##.00;0000
 *
 * With a string like this, number 13 will be formatted into 'fun', number 666 into 'more fun', number 100000 and
 * greater to 1.00M (because ,, mean divide value by 1000^2), number 1000 and greater to 1K. Any other positive
 * numbers not matching existing explicit conditions will be formatted using '##' format. Negative numbers will be
 * formatted using "-##' format and for zero the formatter result will be "0000".
 *
 * The conditions are evaluated as follows:
 *
 * 1. All equals conditions are evaluated first; the order of evaluation matches their order in the format string; left to right
 * 2. All other conditions are evaluated after that; the order of evaluation matches their order in the format string; left to right
 */
export class ConditionalFormat extends FormatterUsingIcu {
    constructor(alternatives, catchall) {
        super();
        this.alternatives = alternatives;
        this.catchall = catchall;
    }
    static doubleToString(value) {
        return parseFloat(value);
    }
    /**
     * Factory method that compiles the provided format string as a number formatter.
     *
     * The compilation will create two variants of the formatter - one compiled using the full format string,
     * another compiled for a format string without all color tags stripped away. These are hidden behind the Formatter
     * interface, and one or the other variant will be used depending on the options passed by the caller.
     *
     * @param matchers - regex matchers
     * @param formatString - formatString to compile
     * @returns always a new instance
     */
    static compile(matchers, factory, alternatives) {
        const conditionMatcher = matchers.getConditionMatcher();
        let defaultFormat = DefaultFormat;
        const compiledEquals = [];
        const compiledOthers = [];
        let rulesWithoutCondition = 0;
        for (const format of alternatives) {
            conditionMatcher.lastIndex = 0;
            let matchResult;
            if ((matchResult = conditionMatcher.exec(format)) !== null) {
                // the alternative format comes with a condition (e.g. [=123]#,#.0)
                const match = DefaultFormatterMatchers.group(matchResult, 0);
                const conditionStart = match.startIndex;
                const conditionEnd = match.endIndex;
                const opMatch = DefaultFormatterMatchers.group(matchResult, 1);
                const op = stringToOperator(opMatch.value);
                const rhsMatch = DefaultFormatterMatchers.group(matchResult, 2);
                const rhs = strToRhs(rhsMatch.value, ConditionalFormat.doubleToString);
                // NULL handling; no explicit test for rhs being 'null' (case insensitive); relying on
                // combination of regex (match either a number or 'null' string)
                if (rhs.maybeValue === null && op !== "Equal") {
                    // alternatives for NULL rhs are only valid if coded as [=NULL]; using any other
                    // operator with NULL means the alternative will never match
                    //
                    // this follows the reference impl which does not check for this case explicitly
                    // but instead works with 'null' and creates a condition code where it compares rhs against
                    // the result of parseFloat('null') which ends up being NaN, which is then appended as a string (also "NaN")
                    // into the generated code...
                    //
                    // see: https://github.com/gooddata/gdc-numberjs/blob/0ab90ee596a854b8ad38fa7956d7148b8c1291df/lib/number.js#L215
                    continue;
                }
                // remove the condition code from the current alternative's format; use this as the effective
                // format for the alternative;
                //
                // following the reference implementation and removing just the first-found occurrence (e.g. whatever
                // was matched)
                //
                // note: the code here cannot assume that the condition is at the beginning of the alternative's
                // part; it can be anywhere; typically it is at the start but there are many format strings in
                // the wild that do not follow this
                let effectiveFormat;
                if (conditionStart === 0) {
                    // this is the happy path, simple copy from end of condition towards the end of the alternative
                    effectiveFormat = format.substring(conditionEnd);
                }
                else {
                    // this has to do two copies; first until the condition start, then from its end to the end of the
                    // string
                    effectiveFormat = format.substring(0, conditionStart);
                    effectiveFormat += format.substring(conditionEnd);
                }
                if (op === "Equal") {
                    if (rhs.maybeValue !== null) {
                        // WEIRD
                        //
                        // this nonsense here mimics the reference impl :( it has what seems to be a bug/hole where
                        // condition rhs 0 is evaluated strictly during equals however condition rhs 0.0 is evaluated
                        // weakly; this impacts how NULL inputs are then handled; strict equality does not match NULLs,
                        // while weak equality does
                        if (rhs.maybeValue === 0 && rhs.inputHadDecimals) {
                            compiledEquals.push(new FormatAlternative("WeakEqual", rhs.maybeValue, factory.compileFormatter(effectiveFormat)));
                        }
                        else {
                            compiledEquals.push(new FormatAlternative(op, rhs.maybeValue, factory.compileFormatter(effectiveFormat)));
                        }
                    }
                    else {
                        compiledEquals.push(new FormatAlternative(op, rhs.maybeValue, FixedLiteralFormatter.compile(matchers, effectiveFormat)));
                    }
                }
                else {
                    compiledOthers.push(new FormatAlternative(op, rhs.maybeValue, factory.compileFormatter(effectiveFormat)));
                }
            }
            else {
                switch (rulesWithoutCondition) {
                    case 0:
                        // intentionally override the default; this mimics the reference impl
                        //
                        // default format will be used for the catchall alternative
                        defaultFormat = format;
                        compiledOthers.push(new FormatAlternative("GreaterThanOrEqual", 0, factory.compileFormatter(format)));
                        break;
                    case 1:
                        compiledOthers.push(new FormatAlternative("LessThan", 0, factory.compileFormatter(format)));
                        break;
                    case 2:
                        compiledEquals.push(new FormatAlternative("Equal", 0, factory.compileFormatter(format)));
                        break;
                    default:
                        throw new InvalidArgumentError("Too many semicolons in the format string.");
                }
                rulesWithoutCondition++;
            }
        }
        // concat all alternatives, making sure that all the alternatives with the EQUAL condition are at the start
        return new ConditionalFormat(compiledEquals.concat(compiledOthers), factory.compileFormatter(defaultFormat));
    }
    doFormat(ctx, number, metadata) {
        const nestedCtx = ctx.toNestedFormatCtx();
        for (const alternative of this.alternatives) {
            if (alternative.evaluate(number, nestedCtx.options.legacyCompatibility)) {
                if (metadata !== undefined && alternative.isNullAlternative()) {
                    metadata.nullConditionFormatter = true;
                }
                return alternative.doFormat(nestedCtx, number, metadata);
            }
        }
        // WEIRD
        //
        // follows the reference implementation but is somewhat surprising for the end-user; the catchall formatter
        // is used for any number that does not match the alternatives; e.g. this applies some default formatting
        //
        // it may easily happen that the value is negative and does not fit any alternatives which would otherwise format
        // it nicely and indicate the negativeness of the value; sending the absolute value of the number to the default
        // catchall can mislead
        return this.catchall.doFormat(nestedCtx, number, metadata);
    }
    doResolve(ctx, number) {
        let effectiveFormat = null;
        const nestedCtx = ctx.toNestedFormatCtx();
        let found = false;
        let nullAlternative = false;
        for (const alternative of this.alternatives) {
            if (alternative.evaluate(number, ctx.options.legacyCompatibility)) {
                found = true;
                nullAlternative = alternative.isNullAlternative();
                effectiveFormat = alternative.doResolve(nestedCtx, number);
                break;
            }
        }
        if (!found) {
            effectiveFormat = this.catchall.doResolve(nestedCtx, number);
        }
        if (effectiveFormat === null) {
            return "";
        }
        // mimics the reference implementation;
        // when the format string from a conditional alternative is used, then it is sent as 'format;format'
        //
        // that is, unless it is for [=null] condition; in that case, the format is returned as is :/
        //
        // the code must make sure to only do this at the top-level conditional; the conditional formats
        // may be nested...
        if (nullAlternative) {
            return effectiveFormat;
        }
        if (ctx.nestedFormatter) {
            return effectiveFormat;
        }
        return `${effectiveFormat};${effectiveFormat}`;
    }
}
function stringToOperator(sign) {
    if (sign === "=") {
        return "Equal";
    }
    else if (sign === ">") {
        return "GreaterThan";
    }
    else if (sign === ">=") {
        return "GreaterThanOrEqual";
    }
    else if (sign === "<") {
        return "LessThan";
    }
    else if (sign === "<=") {
        return "LessThanOrEqual";
    }
    // if this happens then the regex is hosed; it should be such that it only matches
    // the expected operators above
    throw new InvalidArgumentError("Unsupported operator.");
}
function strToRhs(value, converter) {
    // the condition regex is constructed so that the value can be either numeric or 'null'
    // converting to utf8 & then doing stod() should, therefore, be safe as far as unicode format strings
    // are concerned
    const parsedDouble = converter(value);
    const result = {
        maybeValue: null,
        inputHadDecimals: value.indexOf(".") > -1,
    };
    if (Number.isNaN(parsedDouble)) {
        return result;
    }
    result.maybeValue = parsedDouble;
    return result;
}
//# sourceMappingURL=fmt_conditional.js.map